import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import FactorYearReviewService from '../../../../shared/services/factor-year-review/factor-year-review.service';
import { IFactorYearReviewItem, IFactorYearReviewEmission } from '../../../../shared/services/factor-year-review/factor-year-review.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { LoadingSpinner } from '../../../presentation-components/display-elements-components/loading-spinner';

interface FactorYearReviewDialogProps {
  open: boolean;
  item: IFactorYearReviewItem | null;
  onClose: () => void;
  onApplied: (itemId: string) => void;
}

const formatDate = (value?: string): string => (value ? value.slice(0, 10) : '-');
const formatKg = (value?: number | string | null): string => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round(numeric * 100) / 100} kg CO2e` : '-';
};

// FY-6 review step: what the true-up will recalculate, then an explicit apply.
export const FactorYearReviewDialog: React.FC<FactorYearReviewDialogProps> = ({ open, item, onClose, onApplied }) => {
  const [emissions, setEmissions] = useState<IFactorYearReviewEmission[]>([]);
  const [totals, setTotals] = useState<{ emissions: number, calculations: number, amountKg: number }>({ emissions: 0, calculations: 0, amountKg: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const factorYearReviewService = new FactorYearReviewService();
  const reportPeriodService = new ReportPeriodService();
  const userNotificationContext = useContext(UserNotificationsContext);

  useEffect(() => {
    if (open && item) {
      setIsLoading(true);
      setErrorMessage('');
      factorYearReviewService.getItemEmissions(item.id)
        .then(response => {
          setEmissions(response.emissions || []);
          setTotals({
            emissions: response.totalEmissions || 0,
            calculations: response.totalProvisionalCalculations || 0,
            amountKg: Number(response.totalSelectedEmissionAmountKg) || 0,
          });
        })
        .catch(() => setErrorMessage('Could not load the affected emissions.'))
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const handleClose = () => {
    setErrorMessage('');
    onClose();
  };

  const handleApply = async () => {
    if (!item) return;
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const response = await reportPeriodService.updateConversionYear(item.reportPeriodId, {
        conversionSourceId: item.conversionSourceId,
        newYear: item.year,
      });
      if (!response.success) {
        setErrorMessage(response.message || 'Failed to queue the update');
        return;
      }
      userNotificationContext.openSnackbar({
        open: true,
        message: `Recalculating with the ${item.year} factors...`,
        duration: 0,
        backgroundColor: 'primary.main',
      });
      const jobId = response.job?.id;
      if (jobId) {
        const pollInterval = setInterval(async () => {
          try {
            const jobStatus = await reportPeriodService.getConversionYearRecalculationJobStatus(jobId);
            if (jobStatus.status === 'COMPLETED') {
              clearInterval(pollInterval);
              userNotificationContext.openSnackbar({
                open: true,
                message: `Updated to the ${item.year} factors (${jobStatus.recalculatedCount} calculations recalculated)`,
                duration: 5000,
                backgroundColor: 'success.main',
              });
            } else if (jobStatus.status === 'FAILED') {
              clearInterval(pollInterval);
              userNotificationContext.openSnackbar({
                open: true,
                message: jobStatus.errorMessage ? `Update failed: ${jobStatus.errorMessage}` : 'Update failed',
                duration: 8000,
                backgroundColor: 'error.main',
              });
            }
          } catch (error) {
            console.error('Error polling job status:', error);
          }
        }, 2000);
      }
      onApplied(item.id);
      handleClose();
    } catch (error: any) {
      console.error('Error applying factor year update:', error);
      setErrorMessage(error?.response?.data?.message || 'There was an error, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!item) return null;
  const sourceName = item.conversionSource?.source ?? 'conversion factor';

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title={`Update to the ${item.year} factors`}
      cancelHandler={handleClose}
      submitHandler={handleApply}
      isSubmitting={isSubmitting}
      disableSaveButton={isLoading || emissions.length === 0}
      maxWidth='sm'
      submitButtonText={`Update to ${item.year} factors`}
      submitButtonLoadingText='Updating'
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='body1'>
          Applying the {sourceName} {item.year} factors will recalculate {totals.emissions} emission{totals.emissions === 1 ? '' : 's'} ({totals.calculations} provisional calculation{totals.calculations === 1 ? '' : 's'}, currently {formatKg(totals.amountKg)}) and clear their provisional flag.
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><LoadingSpinner /></Box>
        ) : (
          <>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Dates</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align='right'>Provisional calculations</TableCell>
                  <TableCell align='right'>Current amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emissions.map((emission) => (
                  <TableRow key={emission.id}>
                    <TableCell>{formatDate(emission.startDate)} to {formatDate(emission.endDate)}</TableCell>
                    <TableCell>{emission.groupNames || '-'}</TableCell>
                    <TableCell align='right'>{emission.provisionalCalculationCount}</TableCell>
                    <TableCell align='right'>{formatKg(emission.selectedEmissionAmountKg)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totals.emissions > emissions.length && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Showing the first {emissions.length} of {totals.emissions} emissions; the update and the totals above cover all of them.
              </Typography>
            )}
          </>
        )}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Amounts shown are the currently reported (provisional) values; they will be recalculated with the {item.year} factors. This can also be done later from the reporting period settings.
        </Typography>
      </Box>
    </DialogWrapper>
  );
};
